<template>
  <div class="blog-container">
    <!-- Header Section -->
    <div class="blog-header">
      <h1 class="blog-title">{{ $t("blogs") }}</h1>
      <v-btn
        v-if="profileType == 'Agency'"
        color="primary"
        dark
        depressed
        class="create-btn"
        @click="goToCreateBlog"
      >
        <v-icon left small>mdi-plus</v-icon>
        Create Blog
      </v-btn>
    </div>

    <!-- Content Section -->
    <div class="blog-content">
      <!-- Loading State -->
      <v-progress-circular
        v-if="loading && !blogs.length"
        indeterminate
        color="primary"
        size="64"
        width="4"
        class="loading-spinner"
      />

      <!-- Empty State -->
      <div v-else-if="!blogs.length" class="empty-state">
        <v-icon color="#ffff" size="64" class="mb-4">mdi-post-outline</v-icon>
        <h3 class="empty-title">No blogs yet</h3>
        <p class="empty-subtitle">Your creative journey starts here. Share your thoughts with the world!</p>
        <v-btn
          v-if="profileType == 'Agency'"
          color="primary"
          depressed
          class="mt-3"
          @click="goToCreateBlog"
        >
          Create Your First Blog
        </v-btn>
      </div>

      <!-- Blog Grid -->
      <div v-else class="blog-grid">
        <blog-card
          v-for="blog in blogs"
          :key="blog._id"
          :blog="blog"
          class="blog-card"
        />
      </div>

      <!-- Load More Button (if needed in future) -->
      <div v-if="blogs.length > 0 && loadCompleted === 0" class="load-more-container">
        <v-btn
          :loading="loadingMore"
          depressed
          outlined
          color="primary"
          @click="loadMoreEvents"
          class="load-more-btn"
        >
          Load More
        </v-btn>
      </div>
    </div>

    <!-- Notification Snackbars -->
    <v-snackbar v-model="snackbar" :timeout="5000" rounded="pill" elevation="2">
      <div class="snackbar-content">
        <v-icon v-if="profile.status === 'Pending' || profile.status === 'Rejected'" 
                color="warning" class="mr-2">mdi-alert-circle-outline</v-icon>
        <span>{{ errorMessage || message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn color="primary" text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import BlogCard from "./BlogCard.vue";

export default {
  components: { BlogCard },
  data() {
    return {
      loading: false,
      errorMessage: null,
      snackbar: false,
      loadCompleted: -1,
      loadingMore: false,
      message: "",
      token: null,
    };
  },
  methods: {
    ...mapActions("blog", {
      fetchAllBlogs: "fetchAllBlogs",
      loadMore: "loadMoreEvents"
    }),
    showErrorMessage(message) {
      this.errorMessage = message;
      this.snackbar = true;
    },
    loadMoreEvents() {
      this.loadingMore = true;
      this.loadMore({ skip: this.events.length })
        .then(() => {
          setTimeout(() => {
            this.loadingMore = false;
          }, 2500);
        })
        .catch(error => {
          this.loadingMore = false;
          if (error.response == undefined) {
            console.log(error);
          } else {
            this.showErrorMessage(error.response.data.message);
          }
        });
    },
    goToCreateBlog() {
      if (
        this.profile.status === "Pending" ||
        this.profile.status === "Rejected"
      ) {
        this.snackbar = true;
        this.message = "Please wait for admin approval/confirmation";
        return;
      }
      this.$router.push({ name: "create-blog" });
    },
    fetchToken() {
      this.token = localStorage.getItem("user-token");
      if (this.token) {
        //console.log("Token found:", this.token); 
      } else {
        console.log("Token not found in local storage."); 
      }
    },
  },
  computed: {
    ...mapState("auth", {
      profileType: "type",
      profile: "profile"
    }),
    ...mapGetters("blog", {
      blogs: "getAllBlogs"
    })
  },
  mounted() {
    this.loading = true;
    this.loadCompleted = 0;
    if (this.token) {
        this.fetchAllBlogs()
      .then(() => {
        this.loading = false;
      })
      .catch(error => {
        this.loading = false;
        if (error.response == undefined) {
          console.log(error);
        } else {
          this.showErrorMessage(error.response.data.message);
        }
      });
    }
  },
  created() {
    this.fetchToken();
  }
};
</script>

<style scoped>
.blog-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 24px 16px;
}

.blog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
}

.blog-title {
  font-size: 2.25rem;
  font-weight: 700;
  color: #fff;
  margin: 0;
  background: linear-gradient(90deg, #42a5f5, #4fc3f7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.create-btn {
  border-radius: 12px;
  text-transform: none;
  font-weight: 600;
  padding: 8px 20px;
  box-shadow: 0 4px 12px rgba(10, 91, 138, 0.25);
  transition: all 0.3s ease;
}

.create-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(10, 91, 138, 0.35);
}

.blog-content {
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.loading-spinner {
  margin: 80px 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 60px 20px;
  color: #9e9e9e;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: #e0e0e0;
}

.empty-subtitle {
  font-size: 1rem;
  max-width: 300px;
  line-height: 1.5;
}

.blog-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
  width: 100%;
}

.blog-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border-radius: 16px;
  overflow: hidden;
}

.blog-card:hover {
  transform: translateY(-4px);
}

.load-more-container {
  display: flex;
  justify-content: center;
  margin-top: 48px;
  width: 100%;
}

.load-more-btn {
  border-radius: 12px;
  text-transform: none;
  font-weight: 500;
  padding: 8px 24px;
}

.snackbar-content {
  display: flex;
  align-items: center;
}

/* Responsive Design */
@media (max-width: 768px) {
  .blog-container {
    padding: 16px 12px;
  }
  
  .blog-header {
    margin-bottom: 24px;
  }
  
  .blog-title {
    font-size: 1.75rem;
  }
  
  .blog-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .create-btn {
    padding: 6px 16px;
    font-size: 0.875rem;
  }
}

@media (max-width: 480px) {
  .blog-title {
    font-size: 1.5rem;
  }
  
  .empty-title {
    font-size: 1.25rem;
  }
  
  .empty-subtitle {
    font-size: 0.875rem;
  }
}
</style>